import {
  CRMDealCategory,
  CRMDeal,
} from '_business/types/TransactionsDocuments';
import { HubspotDealStatus } from '_business/const/ProjectSteep';
import { Deal } from '_business/types/TransactionDetails';
import { DealCollectionContactToDealItem } from '_business/types/Transactions';

export enum DocumentCategory {
  AVIS_IMPOSITION = 'avis_d_imposition__1_',
  FICHIER_DPE = 'fichier_dpe',
  AUDIT_ENERGETIQUE = 'audit_energetique',
  AUDIT_ENERGETIQUE_FINAL = 'audit_energetique_final',
  GRILLE_ANALYSE_SYNTHESE = 'grille_d_analyse_synthese',
  FICHIER_DEVIS = 'fichier_devis',
  FICHIER_DEVIS_1 = 'fichier_devis__1_',
  PLANS = 'plans',
  RIB_HI = 'rib_hi',
  JUSTIFICATIF_PROPRIETE = 'justificatif_de_propriete',
  AVIS_VIREMENT_HI = 'avis_de_virement_hi',
  ATTESTATION_TRAVAUX_FACTURE = 'attestation_travaux__facture_',
  DEVIS_MAR_SIGNE = 'devis_mar_signe',
  PLAN_FINANCEMENT_RAPPORT_MAR = 'plan_de_financement___rapport_mar',
  PLAN_FINANCEMENT_PREVISIONNEL = 'plan_de_financement_previsionnel',
  FACTURES_HI = 'factures_hi',
  RAPPORT_ACCOMPAGNEMENT_CERFA = 'rapport_d_accompagnement__cerfa_anah_',
  ATTESTATION_TRAVAUX_DEVIS = 'attestation_travaux__devis_',
  ATTESTATION_CEE = 'attestation_cee',
}

export const DEAL_DOCUMENT_CATEGORIES = [
  DocumentCategory.AVIS_IMPOSITION,
  DocumentCategory.FICHIER_DPE,
  DocumentCategory.AUDIT_ENERGETIQUE,
  DocumentCategory.AUDIT_ENERGETIQUE_FINAL,
  DocumentCategory.GRILLE_ANALYSE_SYNTHESE,
  DocumentCategory.FICHIER_DEVIS,
  DocumentCategory.FICHIER_DEVIS_1,
  DocumentCategory.PLANS,
  DocumentCategory.RIB_HI,
  DocumentCategory.JUSTIFICATIF_PROPRIETE,
  DocumentCategory.AVIS_VIREMENT_HI,
  DocumentCategory.ATTESTATION_TRAVAUX_FACTURE,
  DocumentCategory.DEVIS_MAR_SIGNE,
  DocumentCategory.PLAN_FINANCEMENT_RAPPORT_MAR,
  DocumentCategory.PLAN_FINANCEMENT_PREVISIONNEL,
  DocumentCategory.FACTURES_HI,
  DocumentCategory.RAPPORT_ACCOMPAGNEMENT_CERFA,
  DocumentCategory.ATTESTATION_TRAVAUX_DEVIS,
  DocumentCategory.ATTESTATION_CEE,
] as (keyof CRMDealCategory)[];

export type CategoryDocument = (typeof DEAL_DOCUMENT_CATEGORIES)[number];

export const getDocumentDealDocumentCategories = (status: HubspotDealStatus) =>
  [
    DocumentCategory.FICHIER_DEVIS,
    DocumentCategory.AVIS_VIREMENT_HI,
    DocumentCategory.AVIS_IMPOSITION,
    DocumentCategory.JUSTIFICATIF_PROPRIETE,
    DocumentCategory.PLANS,
    DocumentCategory.RIB_HI,
    DocumentCategory.FICHIER_DPE,
    DocumentCategory.AUDIT_ENERGETIQUE,
    DocumentCategory.PLAN_FINANCEMENT_PREVISIONNEL,
    DocumentCategory.PLAN_FINANCEMENT_RAPPORT_MAR,
    DocumentCategory.FACTURES_HI,
    DocumentCategory.RAPPORT_ACCOMPAGNEMENT_CERFA,
    [
      HubspotDealStatus.Travaux,
      HubspotDealStatus.CoherenceFacturesProjet,
      HubspotDealStatus.VisiteFinaleAOrganiser,
      HubspotDealStatus.VisiteFinalePlanifiee,
      HubspotDealStatus.MprDemandeSolde,
      HubspotDealStatus.SubventionsValidees,
      HubspotDealStatus.SubventionsPayees,
    ].includes(status)
      ? DocumentCategory.ATTESTATION_TRAVAUX_FACTURE
      : DocumentCategory.ATTESTATION_TRAVAUX_DEVIS,
    DocumentCategory.ATTESTATION_CEE,
  ] as CategoryDocument[];

export enum CategoryGroupDocument {
  All = 'all',
  Devis = 'devis',
  PreuveVirement = 'preuve_virement',
  PieceAdministratives = 'piece_administratives',
  GrilleAnalyseLogement = 'grille_analyse_logement',
  RestitutionAudit = 'restitution_audit',
  PlanFinancement = 'plan_financement',
  AttestationTravaux = 'attestation_travaux',
  Factures = 'factures',
  RapportAccompagnement = 'rapport_accompagnement',
}

export const getCategoryToUpload = (
  categories: CategoryDocument[],
  status: HubspotDealStatus,
) => {
  if (categories.includes(DocumentCategory.ATTESTATION_TRAVAUX_FACTURE)) {
    return [
      HubspotDealStatus.Travaux,
      HubspotDealStatus.CoherenceFacturesProjet,
      HubspotDealStatus.VisiteFinaleAOrganiser,
      HubspotDealStatus.VisiteFinalePlanifiee,
      HubspotDealStatus.MprDemandeSolde,
      HubspotDealStatus.SubventionsValidees,
      HubspotDealStatus.SubventionsPayees,
    ].includes(status)
      ? DocumentCategory.ATTESTATION_TRAVAUX_FACTURE
      : DocumentCategory.ATTESTATION_TRAVAUX_DEVIS;
  }
  return categories[0];
};

export const DEAL_DOCUMENT_CATEGORIES_GROUPS: [
  CategoryGroupDocument,
  (CategoryDocument[] | CategoryDocument)[],
][] = [
  [
    CategoryGroupDocument.Devis,
    [DocumentCategory.DEVIS_MAR_SIGNE, DocumentCategory.FICHIER_DEVIS],
  ],
  [CategoryGroupDocument.PreuveVirement, [DocumentCategory.AVIS_VIREMENT_HI]],
  [
    CategoryGroupDocument.PieceAdministratives,
    [
      DocumentCategory.AVIS_IMPOSITION,
      DocumentCategory.JUSTIFICATIF_PROPRIETE,
      DocumentCategory.FICHIER_DPE,
      DocumentCategory.PLANS,
      DocumentCategory.RIB_HI,
    ],
  ],
  [
    CategoryGroupDocument.RestitutionAudit,
    [
      DocumentCategory.AUDIT_ENERGETIQUE,
      DocumentCategory.AUDIT_ENERGETIQUE_FINAL,
    ],
  ],
  [
    CategoryGroupDocument.GrilleAnalyseLogement,
    [DocumentCategory.GRILLE_ANALYSE_SYNTHESE],
  ],
  [
    CategoryGroupDocument.AttestationTravaux,
    [
      [
        DocumentCategory.ATTESTATION_TRAVAUX_FACTURE,
        DocumentCategory.ATTESTATION_TRAVAUX_DEVIS,
      ],
      DocumentCategory.ATTESTATION_CEE,
    ],
  ],
  [
    CategoryGroupDocument.PlanFinancement,
    [
      DocumentCategory.PLAN_FINANCEMENT_RAPPORT_MAR,
      DocumentCategory.PLAN_FINANCEMENT_PREVISIONNEL,
    ],
  ],
  [CategoryGroupDocument.Factures, [DocumentCategory.FACTURES_HI]],
  [
    CategoryGroupDocument.RapportAccompagnement,
    [DocumentCategory.RAPPORT_ACCOMPAGNEMENT_CERFA],
  ],
];

export const MAX_DOCUMENT_SIZE_MB = 20;

/**
 * Détermine si un fichier est à déposer selon les règles métier
 * @param category - La catégorie de document à vérifier
 * @param deal - Les données du deal (statut général)
 * @param documentsData - Les données des documents avec le deal CRM
 * @returns true si un fichier est à déposer, false sinon
 */
export const shouldShowToDepositTag = (
  category: CategoryDocument,
  deal: Omit<DealCollectionContactToDealItem, 'associations'>,
): boolean => {
  const dealStatus = deal.dealstage.label?.trim();
  const sousEtapeHi = deal.sous_etape_hi?.label?.trim();

  switch (category) {
    case DocumentCategory.FICHIER_DEVIS:
      // Devis travaux
      // Condition : Phase "Cohérence devis & audit" ET sous-étape "Attente des devis" ET fichier_devis__1_ vide
      return (
        dealStatus === HubspotDealStatus.CoherenceDevisAudit &&
        sousEtapeHi === HubspotDealStatus.AttenteDesDevis &&
        !deal.fichier_devis
      );

    case DocumentCategory.AVIS_VIREMENT_HI:
      // Preuve de virement
      // Condition : Phase "Devis signé" ET avis_de_virement_hi vide
      return (
        dealStatus === HubspotDealStatus.DevisSigne && !deal.avis_de_virement_hi
      );

    case DocumentCategory.AVIS_IMPOSITION:
      // Avis d'imposition
      // Condition : Phase "Devis signé" ET avis_d_imposition__1_ vide
      return (
        dealStatus === HubspotDealStatus.DevisSigne &&
        !deal.avis_d_imposition__1_
      );

    case DocumentCategory.FICHIER_DPE:
      // DPE
      // Condition : Phase "Devis signé" ET fichier_dpe vide
      return dealStatus === HubspotDealStatus.DevisSigne && !deal.fichier_dpe;

    case DocumentCategory.FACTURES_HI:
      // Factures (2 cas possibles)
      if (dealStatus === HubspotDealStatus.CoherenceFacturesProjet) {
        // Cas 1: Factures attendues - Sous-étape "Attente des factures" ET factures_hi vide
        if (
          sousEtapeHi === HubspotDealStatus.AttenteDesFactures &&
          !deal.factures_hi
        ) {
          return true;
        }
        // Cas 2: Factures non conformes - Sous-étape "Factures non conformes"
        if (sousEtapeHi === HubspotDealStatus.FacturesNonConformes) {
          return true;
        }
      }
      return false;

    default:
      return false;
  }
};

export interface DealDocumentStatus {
  totalRequired: number;
  groups: Record<
    CategoryGroupDocument,
    {
      hasRequiredDocuments: boolean;
      categories: Record<string, boolean>; // clé = CategoryDocument jointes par '|'
    }
  >;
}

/**
 * Analyse le deal et retourne les statuts de fichiers requis par groupe de catégories
 * @param deal - Les données du deal (statut général)
 * @returns Un objet avec le nombre total de fichiers requis et les détails par groupe
 */
export const analyzeDealDocumentStatus = (
  deal: Omit<DealCollectionContactToDealItem, 'associations'>,
): DealDocumentStatus => {
  let totalRequired = 0;
  const groups: DealDocumentStatus['groups'] =
    {} as DealDocumentStatus['groups'];

  DEAL_DOCUMENT_CATEGORIES_GROUPS.forEach(([categoryGroup, categories]) => {
    const groupStatus = {
      hasRequiredDocuments: false,
      categories: {} as Record<string, boolean>,
    };

    categories.forEach((category) => {
      const key = Array.isArray(category) ? category.join('|') : category;
      const categoriesToCheck = Array.isArray(category) ? category : [category];

      const hasRequired = categoriesToCheck.some((cat) =>
        shouldShowToDepositTag(cat, deal),
      );

      groupStatus.categories[key] = hasRequired;

      if (hasRequired) {
        groupStatus.hasRequiredDocuments = true;
        totalRequired++;
      }
    });

    groups[categoryGroup] = groupStatus;
  });

  return {
    totalRequired,
    groups,
  };
};
